import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, User, Mail, Phone, CheckCircle2, X } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

export default function ReservationForm({ showFloorTable = false, floors = [], tables = [] }) {
  const {token, user} = useSelector((state) => state.auth);
  const [step, setStep] = useState(1);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [people, setPeople] = useState(2);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [requirements, setRequirements] = useState([]);
const [selectedRequirements, setSelectedRequirements] = useState([]);

  const [options, setOptions] = useState({
    birthday: false,
    highChair: false,
    wheelchair: false,
    anniversary: false,
  });
  const [selectedFloor, setSelectedFloor] = useState(floors[0] ?? "");
  const [selectedTable, setSelectedTable] = useState(tables[0] ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [shakeFields, setShakeFields] = useState({}); // individual shake state

  useEffect(() => {
  if (user && token) {
    setName(user.name || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
  }
}, [user, token]);

  // 🕓 Fetch times
  useEffect(() => {
    if (!date) {
      setAvailableTimes([]);
      setTime("");
      return;
    }
    const fetchTimes = async () => {
      try {
        setLoadingTimes(true);
        const res = await axios.post(`${window.BASE_API_URL}get_reservation_times`, { date });
        const parser = new DOMParser();
        const doc = parser.parseFromString(res.data, "text/html");
        const options = Array.from(doc.querySelectorAll("option"))
          .map((opt) => opt.value)
          .filter((v) => v);

        setAvailableTimes(options);
      } catch (err) {
        console.error("Failed to fetch times", err);
        setAvailableTimes([]);
        toast.error("Failed to load available times");
      } finally {
        setLoadingTimes(false);
      }
    };
    fetchTimes();
  }, [date]);
useEffect(() => {
  const fetchRequirements = async () => {
    try {
      const res = await axios.get(`${window.BASE_API_URL}reservation-requirement`);
      if (res.data?.data) {
        setRequirements(res.data.data);  // [{id,name}, {id,name}]
      }
    } catch (err) {
      console.error("Failed to load requirements", err);
    }
  };

  fetchRequirements();
}, []);

  const emailRegex = /\S+@\S+\.\S+/;

  // Validation helpers
  const validateStep1 = () => {
    const errs = {};
    if (!date) errs.date = "Please select a date";
    if (!time) errs.time = "Please select a time";
    if (!(people > 0)) errs.people = "Please select number of people";
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (name.trim().length < 2) errs.name = "Enter a valid name";
    if (!emailRegex.test(email)) errs.email = "Enter a valid email";
    if (phone.trim().length < 7 || phone.trim().length > 13) errs.phone = "Enter a valid phone number";
    return errs;
  };

  const validateStep3 = () => ({});

  const triggerShake = (fields) => {
    const newShake = {};
    fields.forEach((f) => (newShake[f] = true));
    setShakeFields((prev) => ({ ...prev, ...newShake }));
    setTimeout(() => {
      const reset = {};
      fields.forEach((f) => (reset[f] = false));
      setShakeFields((prev) => ({ ...prev, ...reset }));
    }, 600);
  };

  const validateCurrentStep = () => {
    let errs = {}; validateStep3();

    setErrors(errs);
    if (step === 1) errs = validateStep1();
    else if (step === 2) errs = validateStep2();
    else if (step === 3) errs = validateStep3();
    const fieldsWithError = Object.keys(errs);
    setErrors(errs);
    if (fieldsWithError.length > 0) {
      triggerShake(fieldsWithError);
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateCurrentStep()) return;
    setStep((s) => Math.min(s + 1, 4));
  };

  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const handleSetToday = () => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);
    setTime("");
    toast.info("Date set to today");
  };

  const submitReservation = async () => {
    const allErrors = { ...validateStep1(), ...validateStep2(), ...validateStep3() };
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      triggerShake(Object.keys(allErrors));
      const stepToGo =
        Object.keys(validateStep1()).length > 0
          ? 1
          : Object.keys(validateStep2()).length > 0
          ? 2
          : 3;
      setStep(stepToGo);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        date,
        time,
        people,
        name,
        email,
        phone,
        specialRequests,
        options,
        selectedFloor,
        selectedTable,
        requirements: selectedRequirements.join(",")
      };

      const res = await axios.post(`${window.BASE_API_URL}reservation_submit`, payload);

     if (res.data?.status === "success") {
  toast.success(res.data?.message);

  // ✅ Reset & go back to step 1
  setTimeout(() => {
    setStep(1);
    setDate("");
    setTime("");
    setAvailableTimes([]);
    setPeople(2);
    setName("");
    setEmail("");
    setPhone("");
    setSpecialRequests("");
    setOptions({
      birthday: false,
      highChair: false,
      wheelchair: false,
      anniversary: false,
    });
    setSelectedFloor(floors[0] ?? "");
    setSelectedTable(tables[0] ?? "");
    setSelectedRequirements([]);
    setErrors({});
    setSuccess(false);
  }, 800); // small delay for UX
}
 else {
        toast.error(res.data?.message || "Reservation failed. Try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const ActionButton = ({ onClick, variant = "primary", disabled, children }) => {
  const base =
    "inline-flex items-center gap-2 px-5 py-2 rounded-full font-semibold transition-all";

  const variants = {
    primary:
      "bg-gradient-to-r from-rose-500 to-indigo-500 text-white shadow-md hover:shadow-lg",
    secondary:
      "border border-gray-300 bg-white text-gray-700 hover:bg-gray-100",
    success:
      "bg-green-600 text-white shadow-md hover:bg-green-700",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${variants[variant]} ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
    >
      {children}
    </button>
  );
};


  // Reusable style
  const inputBase =
    "w-full rounded-md bg-gray-50 border py-2 px-3 text-gray-800 focus:outline-none";

  const errorClass = (field) =>
    `${inputBase} ${
      errors[field] ? "border-rose-500" : "border-gray-300"
    } ${shakeFields[field] ? "animate-[shake_0.6s]" : ""}`;

  const StepHeader = () => (
    <div className="w-full">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="text-white bg-gradient-to-r from-rose-500 to-indigo-500 p-2 rounded-lg shadow-md">
            <CalendarDays size={20} />
          </div>
          <div>
            
            <h3 className="text-lg font-semibold text-gray-800">Make a Reservation</h3>
            <p className="text-xs text-gray-500">Step {step} of 4</p>
          </div>
        </div>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-rose-400 to-indigo-400 transition-all"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="flex items-center justify-center p-6 bg-gradient-to-b from-white to-gray-100">
      <div className="max-w-xl w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200"
        >
          <StepHeader />

          <div className="mt-6">
            <AnimatePresence mode="wait">
              {/* STEP 1 */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">Reservation Details</h4>
{date && availableTimes.length == 0 && <p className="text-xs text-rose-600" style={{fontSize:"15px"}}>Restaurant is closed on your specific date and time. Please change your date and time. <br/></p> }
                  {/* Date */}
                  <label className="block mb-3 mt-2">
                    <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                      <CalendarDays size={16} /> Date
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        className={errorClass("date")}
                        value={date}
                        onChange={(e) => {
                          setDate(e.target.value);
                          setTime("");
                          setErrors((p) => ({ ...p, date: undefined }));
                        }}
                        min={new Date().toISOString().split("T")[0]}
                      />
                      <button
                        onClick={handleSetToday}
                        type="button"
                        className="px-3 py-2 bg-rose-500 text-white rounded-md text-sm hover:bg-rose-600"
                      >
                        Set Today
                      </button>
                    </div>

                    {errors.date && <p className="text-xs text-rose-600 mt-1">{errors.date}</p>}
                  </label>

                  {/* Time */}
                  <label className="block mb-3">
                    <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                      Time
                    </div>
                    <select
                      value={time}
                      onChange={(e) => {
                        setTime(e.target.value);
                        setErrors((p) => ({ ...p, time: undefined }));
                      }}
                      className={errorClass("time")}
                      disabled={loadingTimes || !date}
                    >
                      <option value="">
                        {loadingTimes ? "Loading..." : "Select Time"}
                      </option>
                      {availableTimes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {errors.time && <p className="text-xs text-rose-600 mt-1">{errors.time}</p>}
                  </label>

                  {/* People */}
                  <label className="block mb-3">
                    <div className="text-sm text-gray-600 mb-1">No. of people</div>
                    <div
                      className={`inline-flex items-center gap-2 ${
                        shakeFields.people ? "animate-[shake_0.6s]" : ""
                      }`}
                    >
                      <button
                        onClick={() => setPeople((p) => Math.max(1, p - 1))}
                        className="px-3 py-1 rounded-md bg-gray-200"
                      >
                        -
                      </button>
                      <div
                        className={`px-4 py-2 rounded-md ${
                          errors.people ? "bg-rose-50 border border-rose-200" : "bg-gray-50 border border-gray-300"
                        } text-gray-800`}
                      >
                        {people}
                      </div>
                      <button
                        onClick={() => setPeople((p) => Math.min(20, p + 1))}
                        className="px-3 py-1 rounded-md bg-gray-200"
                      >
                        +
                      </button>
                    </div>
                    {errors.people && <p className="text-xs text-rose-600 mt-1">{errors.people}</p>}
                  </label>

                  <div className="mt-4 flex justify-end">
                   <div className="mt-4 flex justify-end">
  <ActionButton onClick={next}>
    Next →
  </ActionButton>
</div>

                  </div>
                </motion.div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">Contact Information</h4>

                  {[
                    { field: "name", label: "Name", icon: <User size={16} />, type: "text", value: name, setter: setName, placeholder: "Full name" },
                    { field: "email", label: "Email", icon: <Mail size={16} />, type: "email", value: email, setter: setEmail, placeholder: "you@example.com" },
                    { field: "phone", label: "Phone", icon: <Phone size={16} />, type: "tel", value: phone, setter: setPhone, placeholder: "Phone number" },
                  ].map(({ field, label, icon, type, value, setter, placeholder }) => (
                    <label key={field} className="block mb-3">
                      <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                        {icon} {label}
                      </div>
                      <input
                        type={type}
                        value={value}
                        onChange={(e) => {
                          setter(e.target.value);
                          setErrors((p) => ({ ...p, [field]: undefined }));
                        }}
                        className={errorClass(field)}
                        placeholder={placeholder}
                      />
                      {errors[field] && <p className="text-xs text-rose-600 mt-1">{errors[field]}</p>}
                    </label>
                  ))}

                 <div className="mt-4 flex justify-between">
  <ActionButton variant="secondary" onClick={prev}>
    ← Back
  </ActionButton>

  <ActionButton onClick={next}>
    Next →
  </ActionButton>
</div>

                </motion.div>
              )}

              {/* STEP 3 */}
             {step === 3 && (
  <motion.div
    key="step3"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 20 }}
    transition={{ type: "spring", stiffness: 300, damping: 30 }}
  >
    <h4 className="text-lg font-semibold mb-3 text-gray-800">Additional Information</h4>

    {/* --- REQUIREMENT LIST SECTION (Show only if > 0 items) --- */}
    {requirements.length > 0 && (
  <div className="mb-4">
    <h5 className="text-md font-semibold text-gray-700 mb-2">Special Request</h5>

    <div className="space-y-2">
      {requirements.map((req) => (
        <label key={req.id} className="flex items-center gap-2 text-gray-700">
          <input
            type="checkbox"
            checked={selectedRequirements.includes(req.name)}   // <-- FIXED
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRequirements((prev) => [...prev, req.name]); // <-- store name
              } else {
                setSelectedRequirements((prev) =>
                  prev.filter((item) => item !== req.name)
                );
              }
            }}
          />
          {req.name}
        </label>
      ))}
    </div>
  </div>
)}


    {/* --- SPECIAL REQUEST TEXTAREA --- */}
    <textarea
      value={specialRequests}
      onChange={(e) => setSpecialRequests(e.target.value)}
      rows={3}
      className="w-full rounded-md bg-gray-50 border border-gray-300 py-2 px-3 text-gray-800"
      placeholder="Special requests..."
    />

   <div className="mt-4 flex justify-between">
  <ActionButton variant="secondary" onClick={prev}>
    ← Back
  </ActionButton>

  <ActionButton onClick={next}>
    Next →
  </ActionButton>
</div>

  </motion.div>
)}


              {/* STEP 4 */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">Booking Summary</h4>

                  <div className="space-y-3 text-sm">
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <div className="text-gray-500">Date & Time</div>
                      <div className="font-medium text-gray-800">
                        {date || "—"} • {time || "—"}
                      </div>
                      <div className="text-xs text-gray-500">{people} people</div>
                    </div>

                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <div className="text-gray-500">Contact</div>
                      <div className="font-medium text-gray-800">{name}</div>
                      <div className="text-xs text-gray-500">
                        {email} • {phone}
                      </div>
                    </div>
                  </div>

                <div className="mt-6 flex justify-between items-center">
                  {!submitting ?
  <ActionButton variant="secondary" onClick={prev}>
    ← Back
  </ActionButton> : '' }

  {!success && (
    <ActionButton
      variant="success"
      onClick={submitReservation}
      disabled={submitting}
    >
      {submitting ? "Requesting..." : "✔ Request reservation"}
    </ActionButton>
  )}
</div>

                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <style>{`
            .btn-primary {
              @apply bg-gradient-to-r from-rose-500 to-indigo-500 text-white px-4 py-2 rounded-md font-semibold shadow-md hover:shadow-lg transition-all;
            }
            .btn-ghost {
              @apply border border-gray-300 px-4 py-2 rounded-md text-gray-700 bg-white hover:bg-gray-100 transition;
            }
            .animate-[shake_0.6s] {
              animation: shake 0.6s;
            }
            @keyframes shake {
              10%,90%{transform:translateX(-1px)}
              20%,80%{transform:translateX(2px)}
              30%,50%,70%{transform:translateX(-4px)}
              40%,60%{transform:translateX(4px)}
            }
          `}</style>
        </motion.div>
      </div>
    </div>
  );
}
